import {useRef, useState} from 'react';
import {Box, HStack, Icon} from '@chakra-ui/react';
import {motion, useReducedMotion} from 'framer-motion';
import {TablerStar} from '../../icons';

// ═══════════════════════════════════════════════════════════════
// StarRatingInput
// ═══════════════════════════════════════════════════════════════
//
// An accessible, animated 1–5 star rating control shared by the Encounter
// Review rating card and the encounter-detail rating modal so the keyboard
// behavior, ARIA semantics, and motion stay identical everywhere.
//
// Accessibility (WCAG 2.1 AA / ADA):
//   • ARIA `radiogroup` with roving tabindex and full keyboard support
//     (←/→/↑/↓, Home/End, Enter/Space).
//   • Per-star labels announce the score AND its meaning ("4 stars, Great").
//   • Selection is conveyed via `aria-checked` + scale, never color alone.
//   • Hit targets are ≥ 44×44px (2.5.5); motion is gated on
//     `prefers-reduced-motion` (2.3.3).

const MotionBox = motion(Box);

export const RATING_DESCRIPTIONS: Record<number, string> = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Great',
  5: 'Excellent',
};

// Amber fill with a darker amber stroke so a filled star clears the 3:1
// non-text contrast bar (1.4.11) against light surfaces.
const STAR_FILL = '#F5A623';
const STAR_STROKE = '#B66E00';

interface StarRatingInputProps {
  value: number;
  onChange: (rating: number) => void;
  onHoverChange?: (rating: number) => void;
  accentColor?: string;
  groupLabel?: string;
  /** Tap-target box size; keep ≥ 44px for WCAG 2.5.5. */
  hitSize?: string;
  starSize?: {base: string; md: string} | string;
  isDisabled?: boolean;
  /** Render a non-interactive display of `value` (no buttons / keyboard). */
  readOnly?: boolean;
}

export default function StarRatingInput({
  value,
  onChange,
  onHoverChange,
  accentColor = '#117DB1',
  groupLabel = 'Rate this session from 1 to 5 stars',
  hitSize = '48px',
  starSize = {base: '32px', md: '36px'},
  isDisabled = false,
  readOnly = false,
}: StarRatingInputProps) {
  const reduceMotion = useReducedMotion();
  const [hoverRating, setHoverRating] = useState(0);
  const [focusedStar, setFocusedStar] = useState(value || 1);
  const starRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const activeRating = hoverRating || value;

  const setHover = (n: number) => {
    setHoverRating(n);
    onHoverChange?.(n);
  };

  const select = (n: number) => {
    if (isDisabled) return;
    setFocusedStar(n);
    onChange(n);
  };

  const handleKeyDown = (e: React.KeyboardEvent, star: number) => {
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = Math.min(5, star + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = Math.max(1, star - 1);
        break;
      case 'Home':
        next = 1;
        break;
      case 'End':
        next = 5;
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        select(star);
        return;
      default:
        return;
    }
    e.preventDefault();
    if (next == null) return;
    select(next);
    starRefs.current[next - 1]?.focus();
  };

  if (readOnly) {
    return (
      <HStack
        spacing={1}
        role="img"
        aria-label={`Rated ${value} out of 5 stars`}
      >
        {[1, 2, 3, 4, 5].map(star => {
          const filled = star <= value;
          return (
            <Icon
              key={star}
              as={TablerStar}
              boxSize={starSize}
              color={filled ? STAR_FILL : 'gray.400'}
              fill={filled ? STAR_FILL : 'transparent'}
              stroke={filled ? STAR_STROKE : undefined}
              aria-hidden="true"
            />
          );
        })}
      </HStack>
    );
  }

  return (
    <HStack
      spacing={{base: 0, sm: 1}}
      role="radiogroup"
      aria-label={groupLabel}
      onMouseLeave={() => setHover(0)}
    >
      {[1, 2, 3, 4, 5].map(star => {
        const filled = star <= activeRating;
        const isChosen = star === value;
        return (
          <MotionBox
            key={star}
            as="button"
            type="button"
            ref={(el: HTMLButtonElement | null) => {
              starRefs.current[star - 1] = el;
            }}
            onClick={() => select(star)}
            onKeyDown={(e: React.KeyboardEvent) => handleKeyDown(e, star)}
            onMouseEnter={() => setHover(star)}
            role="radio"
            aria-checked={isChosen}
            aria-label={`${star} ${star === 1 ? 'star' : 'stars'}, ${
              RATING_DESCRIPTIONS[star]
            }`}
            aria-disabled={isDisabled || undefined}
            tabIndex={focusedStar === star ? 0 : -1}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            minW={hitSize}
            minH={hitSize}
            borderRadius="lg"
            bg="transparent"
            cursor={isDisabled ? 'default' : 'pointer'}
            _focusVisible={{
              boxShadow: `0 0 0 3px ${accentColor}66`,
              outline: 'none',
            }}
            animate={reduceMotion ? undefined : {scale: isChosen ? 1.18 : 1}}
            whileHover={reduceMotion || isDisabled ? undefined : {scale: 1.12}}
            whileTap={reduceMotion || isDisabled ? undefined : {scale: 0.9}}
            transition={{type: 'spring', stiffness: 500, damping: 18}}
          >
            <Icon
              as={TablerStar}
              boxSize={starSize}
              color={filled ? STAR_FILL : 'gray.400'}
              fill={filled ? STAR_FILL : 'transparent'}
              stroke={filled ? STAR_STROKE : undefined}
              aria-hidden="true"
            />
          </MotionBox>
        );
      })}
    </HStack>
  );
}
